import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { motion, AnimatePresence } from "framer-motion";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Image } from "primereact/image";

import FSReportConfigModal from "./FSReportConfigModal";
import DocumentViewModal from "../../Components/DocumentViewModal";
import TextInput from "../../Components/TextInput";
import ErrorPage from "../../Components/ErrorPage";
import NoDataCreated from "../../Components/NoDataCreated";
import { formatDate } from "../../Utils/getDate";
import { goToTop } from "../../Components/goToTop";
import { noImageIcon } from "../../Utils/appData";

import { FeatureOption, FSProject, FSReportData } from "../../Controllers/Types/FinishingSchedule/FinishingSchedule";
import { FSFileList } from "../../Controllers/Types/FinishingSchedule/FileList";
import { fetchFSReportData } from "../../Controllers/Thunks/FinishingScheduleThunks";
import { FSPaint } from "../../Controllers/Types/FinishingSchedule/Paint/Paint";
import { FSDoor } from "../../Controllers/Types/FinishingSchedule/Door/Door";
import { FSKitchen } from "../../Controllers/Types/FinishingSchedule/Kitchen/Kitchen";
import { FSLight } from "../../Controllers/Types/FinishingSchedule/Light/Light";
import { FSBathroom } from "../../Controllers/Types/FinishingSchedule/Bathroom/Bathroom";
import { FSTile } from "../../Controllers/Types/FinishingSchedule/Tile/Tile";
import { FSFloorFinishingData } from "../../Controllers/Types/FinishingSchedule/FloorFinishing/FloorFinishing";
import { FSOther } from "../../Controllers/Types/FinishingSchedule/Other/Other";
import { FSDataOrder } from "../../Controllers/Types/FinishingSchedule/Order";
import { FSAccessoryData } from "../../Controllers/Types/FinishingSchedule/Accessory/AccessoryData";
import { sendFSReportViaEmail } from "../../Api/FinishingScheduleService/FinishingScheduleService";

type FSSectionName =
    | "Light"
    | "Door"
    | "Paint"
    | "Bathroom"
    | "Kitchen"
    | "Tiles"
    | "Floor Finishing"
    | "Others";

interface EmailInput {
    value: string;
    error: string;
}

const FinishingScheduleReport: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const today = new Date();
    const dispatch = useAppDispatch();

    const [fsData, setFSData] = useState<FSProject | null>(null);
    const [selectedFeatureOptions, setSelectedFeatureOptions] = useState<FeatureOption[]>([]);
    const [showReportConfigModal, setShowReportConfigModal] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [showMailModal, setShowMailModal] = useState<boolean>(false);
    const [emailAddresses, setEmailAddresses] = useState<EmailInput[]>([{ value: '', error: '' },]);
    const [showFileViewModal, setShowFileViewModal] = useState<boolean>(false);
    const [fileUrl, setFileUrl] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    const loadingFSReportData: boolean = useAppSelector((state) => state?.finishingSchedule?.loadingFSReportData);
    const errorFSReportData: string | null = useAppSelector((state) => state?.finishingSchedule?.errorFSReportData);
    const fsReportData: FSReportData | null = useAppSelector((state) => state?.finishingSchedule?.fsReportData);

    const [fsLightData, setFSLightData] = useState<FSLight[]>([]);
    const [fsDoorData, setFSDoorData] = useState<FSDoor[]>([]);
    const [fsPaintData, setFSPaintData] = useState<FSPaint[]>([]);
    const [fsBathroomData, setFSBathroomData] = useState<FSBathroom[]>([]);
    const [fsKitchenData, setFSKitchenData] = useState<FSKitchen[]>([]);
    const [fsKitchenApplianceData, setFSKitchenApplianceData] = useState<FSKitchen[]>([]);
    const [fsTileData, setFSTileData] = useState<FSTile[]>([]);
    const [fsFloorFinishingData, setFSFloorFinishingData] = useState<FSFloorFinishingData[]>([]);
    const [fsOtherData, setFSOtherData] = useState<FSOther[]>([]);

    const [doorAccessoryData, setDoorAccessoryData] = useState<FSDoor | null>(null);
    const [paintAccessoryData, setPaintAccessoryData] = useState<FSPaint | null>(null);
    const [tileAccessoryData, setTileAccessoryData] = useState<FSTile | null>(null);
    const [floorFinishingAccessoryData, setFloorFinishingAccessoryData] = useState<FSAccessoryData[]>([]);
    const [hasFloorFinishingAccessoryOption, setHasFloorFinishingAccessoryOption] = useState(false);

    const [expandedSections, setExpandedSections] = useState<FSSectionName[]>([]);
    const sectionRefs = useRef<Record<FSSectionName, HTMLDivElement | null>>({
        Light: null,
        Door: null,
        Paint: null,
        Bathroom: null,
        Kitchen: null,
        Tiles: null,
        "Floor Finishing": null,
        Others: null,
    });


    /* ************************************************************* Permission handler */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForGenerateReport = useCanEdit("Finishing Schedule", "generate_report");
    const hasPermissionForDownloadReport = useCanEdit("Finishing Schedule", "download_report");
    const hasPermissionForSendReport = useCanEdit("Finishing Schedule", "send_report");
    /* ************************************************************ */


    useEffect(() => {
        const state = location?.state;
        if (!state) return;

        setFSData(state.fsData || null);
        setSelectedFeatureOptions(state.selectedFeatureOptions || []);
    }, [location.state]);

    useEffect(() => {
        if (selectedFeatureOptions.length > 0) {
            dispatch(fetchFSReportData({ generateReport: 0, featureOptions: selectedFeatureOptions }));
        }
    }, [selectedFeatureOptions, dispatch]);

    useEffect(() => {
        if (!fsReportData?.data) {
            setFSLightData([]);
            setFSDoorData([]);
            setDoorAccessoryData(null);
            setFSPaintData([]);
            setPaintAccessoryData(null);
            setFSBathroomData([]);
            setFSKitchenData([]);
            setFSKitchenApplianceData([]);
            setFSTileData([]);
            setTileAccessoryData(null);
            setFSFloorFinishingData([]);
            setFloorFinishingAccessoryData([]);
            setFSOtherData([]);
            return;
        }

        const data = fsReportData.data;

        // Light
        setFSLightData(data["Light"] ?? []);

        // Door
        const doors = data["Door"] ?? [];

        const doorData = doors.filter(d => d.location_name?.toLowerCase() !== "accessory");
        setFSDoorData(doorData ?? []);

        const doorAccessory = doors.find(d => d.location_name?.toLowerCase() === "accessory");
        setDoorAccessoryData(doorAccessory ?? null);

        // Paint
        const paints = data["Paint"] ?? [];

        const paintData = paints.filter(p => p.location_name?.toLowerCase() !== "accessory");
        setFSPaintData(paintData ?? []);

        const paintAccessory = paints.find(p => p.location_name?.toLowerCase() === "accessory");
        setPaintAccessoryData(paintAccessory ?? null);

        // Bathroom
        setFSBathroomData(data["Bathroom"] ?? []);

        // Kitchen & Appliances
        const kitchens = data["Kitchen"] ?? [];
        setFSKitchenData(kitchens.filter(k => k.location_name?.toLowerCase() === "kitchen"));
        setFSKitchenApplianceData(kitchens.filter(k => k.location_name?.toLowerCase() === "appliances"));

        // Tiles
        const tiles = data["Tiles"] ?? [];

        const tileData = tiles.filter(t => t.location_name?.toLowerCase() !== "accessory");
        setFSTileData(tileData ?? []);

        const tileAccessory = tiles.find(t => t.location_name?.toLowerCase() === "accessory");
        setTileAccessoryData(tileAccessory ?? null);

        // Floor Finishing
        const floorFinishing = data["Floor Finishing"];
        if (floorFinishing) {
            setFSFloorFinishingData(floorFinishing.data_ff ?? []);

            if ("data_accessory" in floorFinishing) {
                setHasFloorFinishingAccessoryOption(true);
                setFloorFinishingAccessoryData(floorFinishing.data_accessory ?? []);
            } else {
                setHasFloorFinishingAccessoryOption(false);
                setFloorFinishingAccessoryData([]);
            }
        } else {
            setFSFloorFinishingData([]);
            setHasFloorFinishingAccessoryOption(false);
            setFloorFinishingAccessoryData([]);
        }

        // Others
        setFSOtherData(data["Others"] ?? []);
    }, [fsReportData]);

    const handleShowReportConfigModal = () => {
        setShowReportConfigModal(true);
    }

    const handleCloseReportConfigModal = () => {
        setShowReportConfigModal(false);
    }

    const handlePreviewUpdate = (fsData: FSProject, newOptions: FeatureOption[]) => {
        setSelectedFeatureOptions(newOptions);
        handleCloseReportConfigModal();
    };

    useEffect(() => {
        if (fsReportData?.data) {
            const availableSections = Object.keys(fsReportData.data) as FSSectionName[];
            if (availableSections.length > 0) {
                setExpandedSections([availableSections[0]]);
            } else {
                setExpandedSections([]);
            }
        } else {
            setExpandedSections([]);
        }
    }, [fsReportData]);

    const toggleSection = (section: FSSectionName) => {
        setExpandedSections((prev) => {
            const isExpanded = prev.includes(section);
            const newExpanded = isExpanded
                ? prev.filter((s) => s !== section)
                : [...prev, section];

            if (!isExpanded) {
                const sectionElement = sectionRefs.current[section];
                if (sectionElement) {
                    sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }
            return newExpanded;
        });
    };

    const handleGenerateReport = () => {
        confirmDialog({
            message: 'Are you sure, want to generate the finishing schedlue report?',
            header: 'Confirmation',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-success',
            accept: () => generateReport(),
            resizable: false,
            draggable: false,
        });
    }

    const generateReport = async () => {
        if (selectedFeatureOptions.length > 0) {
            dispatch(fetchFSReportData({ generateReport: 1, featureOptions: selectedFeatureOptions }));
        }
    }

    /* ************************************************************ Send report */
    const handleCloseMailModal = () => {
        setShowMailModal(false);
        setEmailAddresses([{ value: '', error: '' }]);
    };

    const handleSendReport = () => {
        setShowMailModal(true);

        const project_id: number = fsData?.project_id!;
        const fs_report_download_path: string = fsReportData?.fs_report_download_path!;
        const email_list: string[] = emailAddresses.map((email) => email.value);

        console.log('project_id', project_id);
        console.log('fs_report_download_path', fs_report_download_path);
        console.log('email_list', email_list);
    };

    // validations.
    const validateEmail = (email: string): string => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return 'Email is required.';
        if (!emailRegex.test(email)) return 'Enter a valid email.';
        return '';
    };

    function checkCredentialsEmail(): boolean {
        const updatedEmails = emailAddresses.map((email) => {
            const error = validateEmail(email.value);
            return { ...email, error };
        });

        setEmailAddresses(updatedEmails);

        const hasErrors = updatedEmails.some(email => email.error !== '');
        return !hasErrors;
    }

    function showAPIErrorEmail(keyName: string, error: string): void {
        if (keyName === "email_list[]") {
            const updatedEmails = emailAddresses.map((email) => ({
                ...email,
                error,
            }));
            setEmailAddresses(updatedEmails);
        }
    }

    // Send email API
    const handleSendEmail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsEmail() || fsData === null || fsReportData === null) return;

        setLoading(true);

        try {
            const project_id: number = fsData?.project_id;
            const fs_report_download_path: string = fsReportData?.fs_report_download_path!;
            const email_list: string[] = emailAddresses.map((email) => email.value);

            const data = await sendFSReportViaEmail(
                project_id,
                fs_report_download_path,
                email_list
            );

            if (data.statusCode === 200) {
                handleCloseMailModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Email sent successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorEmail(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error sending the email.",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error sending the email:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while sending the email.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleAddMail = () => {
        const lastEmail = emailAddresses[emailAddresses.length - 1];
        const validationMessage = validateEmail(lastEmail.value);

        if (validationMessage) {
            const updatedEmails = [...emailAddresses];
            updatedEmails[updatedEmails.length - 1].error = validationMessage;
            setEmailAddresses(updatedEmails);
            return;
        }

        setEmailAddresses([...emailAddresses, { value: '', error: '' }]);
    };

    const handleRemoveMail = (index: number) => {
        if (emailAddresses.length === 1) return;
        const updatedEmails = emailAddresses.filter((_, i) => i !== index);
        setEmailAddresses(updatedEmails);
    };

    const handleEmailChange = (index: number, newValue: string) => {
        const updatedEmails = [...emailAddresses];
        updatedEmails[index].value = newValue;
        updatedEmails[index].error = '';
        setEmailAddresses(updatedEmails);
    };

    const mailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Send report via mail</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseMailModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const mailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseMailModal}
            />
            <Button
                loading={loading}
                label={`${loading ? "Sending" : "Send"}`}
                className="custom_btn primary"
                onClick={handleSendEmail}
            />
        </div>
    );
    /* ************************************************************ */


    /* ************************************************************ View report */
    const handleViewReportDocument = () => {
        setFileUrl(fsReportData?.fs_report_download_path ?? '');
        setShowFileViewModal(true);
    };

    const handleCloseFileViewModal = () => {
        setShowFileViewModal(false);
        setFileUrl('');
    };
    /* ************************************************************ */


    /* ************************************************************ Download report */
    const handleDownloadReport = async () => {
        if (isDownloading) return;

        const reportPath = fsReportData?.fs_report_download_path?.trim() || null;

        if (!reportPath) {
            toast.current?.show({
                severity: "error",
                summary: "Failed",
                detail: "Report file not found!",
                life: 3000,
            });
            return;
        }

        setIsDownloading(true);

        try {
            await new Promise((resolve) => setTimeout(resolve, 300));

            const response = await fetch(reportPath, { cache: "no-store" });
            if (!response.ok) throw new Error(`Failed to fetch file (${response.status})`);

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");

            const fileName =
                reportPath.split("/").pop() ||
                `Finishing-Schedule-Report-${formatDate(today!)}.pdf`;

            link.href = blobUrl;
            link.download = fileName;
            link.style.display = "none";

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);

            toast.current?.show({
                severity: "success",
                summary: "Downloaded",
                detail: "Report downloaded successfully.",
                life: 3000,
            });
        } catch (error) {
            console.error("Download error:", error);
            toast.current?.show({
                severity: "error",
                summary: "Failed",
                detail: "Unable to download report. Please try again.",
                life: 3000,
            });
        } finally {
            setIsDownloading(false);
        }
    };
    /* ************************************************************ */


    /* ************************************************************ Table content */
    const tableImageBody = (imagePath: string | null) => {
        return (
            <span className="table_display_outer sm mx-auto">
                {imagePath ? (
                    <Image
                        src={imagePath}
                        alt="Image"
                        preview
                        className="table_img_display"
                        loading="lazy"
                        draggable={false}
                        unstyled={false}
                    />
                ) : (
                    <img src={noImageIcon} className="table_img_empty" alt="N/A" draggable={false} />
                )}
            </span>
        )
    }

    const tableColorBody = (colorCode: string | null) => {
        return (
            <span className="table_display_outer sm mx-auto">
                <span className="table_color_display" style={{ backgroundColor: colorCode || '#FFF' }}>
                    {colorCode === null && <i className="bi bi-exclamation-circle"></i>}
                </span>
            </span>
        )
    }

    const handleViewFile = (filePath: string) => {
        setFileUrl(filePath);
        setShowFileViewModal(true);
    }

    const tableFilesBody = (
        files: FSFileList[],
        feature: 'Light' | 'Door' | 'Paint' | 'Bathroom' | 'Kitchen' | 'Tiles' | 'Floor Finishing' | 'Others',
        isAccessory: boolean
    ) => {
        return (
            <div className="item_file_area justify-content-center" style={{ maxWidth: '100%' }}>
                {files && files?.length > 0 ? (
                    <React.Fragment>
                        {files?.map((file) => {
                            return (
                                <React.Fragment>
                                    {file?.extension === "pdf" ? (
                                        <div className="item_file_label" key={`${feature}${isAccessory ? 'Accessory' : ''}_file_${file?.index}`}>
                                            <div
                                                className="item_doc p-ripple"
                                                onClick={() => handleViewFile(file?.file_path!)}
                                            >
                                                <i className="bi bi-file-earmark-pdf"></i>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="item_file_label" key={`${feature}_file_${file?.index}`}>
                                            <Image
                                                src={`${file?.file_path}`}
                                                alt={`Image`}
                                                className="item_image"
                                                preview
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        }
                        )}
                    </React.Fragment>
                ) : (
                    <div className="no_file_found">
                        ----
                    </div>
                )}
            </div>
        )
    }

    const tableOrderDataRow = (
        orderData: FSDataOrder | null,
        colSpan: number,
        feature: 'Light' | 'Door' | 'Paint' | 'Bathroom' | 'Kitchen' | 'Tiles' | 'Floor Finishing' | 'Others'
    ) => {
        return (
            <React.Fragment>
                {orderData && orderData !== null && (
                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                        <td colSpan={colSpan} className="data_column">
                            Order
                        </td>
                    </tr>
                )}
            </React.Fragment>
        )
    }
    /* ************************************************************ */


    /* ************************************************************ Report content */
    const accessoryDataBody = (
        accessoryData: FSAccessoryData[],
        feature: 'Door' | 'Paint' | 'Tiles' | 'Floor Finishing'
    ) => {
        return (
            <React.Fragment key={`${feature}Accessory`}>
                <div className="fs_report_label">
                    <h6>Accessory</h6>
                </div>

                <div className="fs_report_table_area">
                    <div className="table-responsive">
                        <table className="table table-bordered sv_report_table pl_table mb-0">
                            {accessoryData && accessoryData?.length > 0 ? (
                                <tbody>
                                    {accessoryData?.map((accessory) => (
                                        <React.Fragment key={`${feature}Accessory_${accessory?.id}`}>
                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                <td style={{ width: "10%" }}>
                                                    {tableImageBody(accessory?.image_path || null)}
                                                </td>

                                                <td style={{ width: "15%" }} className="data_column">
                                                    {accessory?.accessory?.accessory_name ?? "---"}
                                                </td>

                                                <td style={{ width: "15%" }} className="data_column">
                                                    {accessory?.accessory_sub?.accessory_sub_name ?? "---"}
                                                </td>

                                                <td style={{ width: "10%" }} className="data_column text-center">
                                                    {accessory?.number ? String(accessory?.number)?.padStart(2, '0') : "---"}
                                                </td>

                                                <td style={{ width: "15%" }} className="data_column note_column">
                                                    {accessory?.reference ?? "---"}
                                                </td>

                                                <td style={{ width: "15%" }} className="data_column">
                                                    {accessory?.link ? (
                                                        <Link to={accessory?.link} target="_blank">
                                                            {accessory?.link ?? "---"}
                                                        </Link>
                                                    ) : (
                                                        <span>---</span>
                                                    )}
                                                </td>

                                                <td style={{ width: "20%" }}>
                                                    {tableFilesBody(accessory?.file_list, feature, true)}
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            ) : (
                                <tbody>
                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                        <td className="text-center text-muted">
                                            No {(feature)?.toLowerCase()} accessory details found.
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    const reportContentHeader = (title: FSSectionName) => {
        return (
            <div
                key={title}
                className="fs_report_header"
                onClick={() => toggleSection(title)}>
                <h6>
                    <i className={`bi bi-caret-right-fill ${expandedSections.includes(title) ? 'expanded' : ''} me-2`}></i>
                    {title}
                </h6>
            </div>
        )
    }
    /* ************************************************************ */


    /* ************************************************************ Common functions */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* ************************************************************ */
    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {loadingFSReportData ? (
                    <div className="loading_container">
                        <ProgressSpinner
                            className="progresscircle custom"
                            style={{ width: '50px', height: '50px', stroke: '#8953D7' }}
                            strokeWidth="8" fill="var(--surface-ground)"
                            animationDuration="1s"
                        />
                    </div>
                ) : errorFSReportData ? (
                    <ErrorPage errorMessage={errorFSReportData} containerClassname="full_height" />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {fsData ? `${fsData?.job_number}: ${fsData?.address}` : ''}
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    Finishing schedule
                                </li>

                                <li className="page_breadcrumb_item active">
                                    Report
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-2">
                            <div className="page_action_area">
                                {fsReportData && fsReportData?.fs_report_download_path && (
                                    <React.Fragment>
                                        {hasPermissionForDownloadReport && (
                                            <button
                                                className="add_data_link_btn custom p-ripple"
                                                onClick={handleDownloadReport}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <ProgressSpinner
                                                        style={{ width: "20px", height: "20px" }}
                                                        strokeWidth="5"
                                                        fill="none"
                                                        animationDuration="1s"
                                                        className="spin_loader"
                                                    />
                                                ) : (
                                                    <i className="bi bi-download"></i>
                                                )}
                                                <span className="btn_text">{isDownloading ? "Downloading" : "Download"}</span>
                                                <Ripple />
                                            </button>
                                        )}

                                        {hasPermissionForSendReport && (
                                            <button
                                                className="add_data_link_btn custom p-ripple"
                                                onClick={handleSendReport}
                                            >
                                                <i className="bi bi-envelope"></i>
                                                <span className="btn_text">Send </span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}

                                <button
                                    className="add_data_link_btn custom p-ripple"
                                    onClick={handleShowReportConfigModal}
                                >
                                    <i className="bi bi-eye"></i>
                                    <span className="btn_text">Preview </span>
                                    <Ripple />
                                </button>

                                {hasPermissionForGenerateReport && (
                                    <button
                                        className="add_data_link_btn custom p-ripple"
                                        onClick={handleGenerateReport}
                                    >
                                        <i className="bi bi-gear"></i>
                                        <span className="btn_text">Generate </span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        {fsData && fsData !== null ? (
                            <article className="fs_report_container">
                                {/* Light */}
                                {fsReportData && fsReportData?.data?.["Light"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Light"] = el)}>
                                        {reportContentHeader('Light')}

                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Light") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {fsLightData && fsLightData?.length > 0 && (
                                                        fsLightData?.map((lightData) => (
                                                            <React.Fragment key={`lightFeature_${lightData?.id}`}>
                                                                <div className="fs_report_label">
                                                                    <h6>{lightData?.location_name}</h6>
                                                                </div>

                                                                <div className="fs_report_table_area">
                                                                    <div className="table-responsive">
                                                                        <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                            {lightData?.data_light && lightData?.data_light?.length > 0 ? (
                                                                                <tbody>
                                                                                    {lightData?.data_light?.map((light) => (
                                                                                        <React.Fragment key={`light_${light?.id}`}>
                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                <td style={{ width: "10%" }}>
                                                                                                    {tableImageBody(light?.image_path || null)}
                                                                                                </td>

                                                                                                <td style={{ width: "20%" }} className="data_column">
                                                                                                    {light?.item?.item_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "20%" }} className="data_column">
                                                                                                    {light?.type?.type_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "10%" }} className="data_column text-center">
                                                                                                    {light?.number ? String(light?.number)?.padStart(2, '0') : "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {light?.link ? (
                                                                                                        <Link to={light?.link} target="_blank">
                                                                                                            {light?.link ?? "---"}
                                                                                                        </Link>
                                                                                                    ) : (
                                                                                                        <span>---</span>
                                                                                                    )}
                                                                                                </td>

                                                                                                <td style={{ width: "25%" }}>
                                                                                                    {tableFilesBody(light?.file_list, 'Light', false)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        </React.Fragment>
                                                                                    ))}
                                                                                </tbody>
                                                                            ) : (
                                                                                <tbody>
                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                        <td className="text-center text-muted">
                                                                                            No {(lightData?.location_name)?.toLowerCase()} light details found.
                                                                                        </td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            )}
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        ))
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Door */}
                                {fsReportData && fsReportData?.data?.["Door"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Door"] = el)}>
                                        {reportContentHeader('Door')}

                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Door") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {fsDoorData && fsDoorData?.length > 0 && (
                                                        fsDoorData?.map((doorData) => (
                                                            <React.Fragment key={`doorFeature_${doorData?.id}`}>
                                                                <div className="fs_report_label">
                                                                    <h6>{doorData?.location_name}</h6>
                                                                </div>

                                                                <div className="fs_report_table_area">
                                                                    <div className="table-responsive">
                                                                        <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                            {doorData?.data_door && doorData?.data_door?.length > 0 ? (
                                                                                <tbody>
                                                                                    {doorData?.data_door?.map((door) => (
                                                                                        <React.Fragment key={`door_${door?.id}`}>
                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                <td style={{ width: "10%" }}>
                                                                                                    {tableImageBody(door?.image_path || null)}
                                                                                                </td>

                                                                                                <td style={{ width: "12%" }} className="data_column">
                                                                                                    {door?.color?.color_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "8%" }} className="data_column text-center">
                                                                                                    {door?.fire_resistance_rating ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "20%" }} className="data_column">
                                                                                                    {door?.size_type === 'Standard size' ? (door?.size?.size_name ?? '---') : (door?.manual_size ?? '---')}
                                                                                                </td>

                                                                                                <td style={{ width: "12%" }} className="data_column text-center">
                                                                                                    {door?.status ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "8%" }} className="data_column text-center">
                                                                                                    {door?.number ? String(door?.number)?.padStart(2, '0') : "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column note_column">
                                                                                                    {door?.note ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }}>
                                                                                                    {tableFilesBody(door?.file_list, 'Door', false)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        </React.Fragment>
                                                                                    ))}
                                                                                </tbody>
                                                                            ) : (
                                                                                <tbody>
                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                        <td className="text-center text-muted">
                                                                                            No door {(doorData?.location_name)?.toLowerCase()} details found.
                                                                                        </td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            )}
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        ))
                                                    )}

                                                    {doorAccessoryData && (
                                                        accessoryDataBody(doorAccessoryData?.data_accessory, 'Door')
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Paint */}
                                {fsReportData && fsReportData?.data?.["Paint"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Paint"] = el)}>
                                        {reportContentHeader('Paint')}

                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Paint") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {fsPaintData && fsPaintData?.length > 0 && (
                                                        fsPaintData?.map((paintData) => (
                                                            <React.Fragment key={`paintFeature_${paintData?.id}`}>
                                                                <div className="fs_report_label">
                                                                    <h6>{paintData?.location_name}</h6>
                                                                </div>

                                                                <div className="fs_report_table_area">
                                                                    <div className="table-responsive">
                                                                        <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                            {paintData?.data_paint && paintData?.data_paint?.length > 0 ? (
                                                                                <tbody>
                                                                                    {paintData?.data_paint?.map((paint) => (
                                                                                        <React.Fragment key={`paint_${paint?.id}`}>
                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                <td style={{ width: "10%" }}>
                                                                                                    {tableColorBody(paint?.color?.color_code || null)}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {paint?.type ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {paint?.brand?.brand_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {paint?.color?.color_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {paint?.location?.location_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {paint?.link ? (
                                                                                                        <Link to={paint?.link} target="_blank">
                                                                                                            {paint?.link ?? "---"}
                                                                                                        </Link>
                                                                                                    ) : (
                                                                                                        <span>---</span>
                                                                                                    )}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }}>
                                                                                                    {tableFilesBody(paint?.file_list, 'Paint', false)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        </React.Fragment>
                                                                                    ))}
                                                                                </tbody>
                                                                            ) : (
                                                                                <tbody>
                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                        <td className="text-center text-muted">
                                                                                            No {(paintData?.location_name)?.toLowerCase()} paint details found.
                                                                                        </td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            )}
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        ))
                                                    )}

                                                    {paintAccessoryData && (
                                                        accessoryDataBody(paintAccessoryData?.data_accessory, 'Paint')
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Bathroom */}
                                {fsReportData && fsReportData?.data?.["Bathroom"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Bathroom"] = el)}>
                                        {reportContentHeader('Bathroom')}

                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Bathroom") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {fsBathroomData && fsBathroomData?.length > 0 && (
                                                        fsBathroomData?.map((bathroomData) => (
                                                            <React.Fragment key={`bathroomFeature_${bathroomData?.id}`}>
                                                                <div className="fs_report_label">
                                                                    <h6>{bathroomData?.location_name}</h6>
                                                                </div>

                                                                {bathroomData?.feature_list && bathroomData?.feature_list?.length > 0 && (
                                                                    bathroomData?.feature_list?.map((bathroomFeature) => (
                                                                        <React.Fragment key={`bathroomFeature_${bathroomFeature?.id}`}>
                                                                            <div className="fs_report_sub_label">
                                                                                <h6>
                                                                                    <i className="bi bi-arrow-return-right me-2"></i>
                                                                                    {bathroomFeature?.feature_name
                                                                                        ?.toLowerCase()
                                                                                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                                                                                </h6>
                                                                            </div>

                                                                            <div className="fs_report_table_area">
                                                                                <div className="table-responsive">
                                                                                    <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                        {bathroomFeature?.data_bathroom && bathroomFeature?.data_bathroom?.length > 0 ? (
                                                                                            <tbody>
                                                                                                {bathroomFeature?.data_bathroom?.map((bathroom) => (
                                                                                                    <React.Fragment key={`bathroom_${bathroom?.id}`}>
                                                                                                        <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                            <td style={{ width: "10%" }}>
                                                                                                                {tableImageBody(bathroom?.image_path || null)}
                                                                                                            </td>

                                                                                                            <td style={{ width: "12%" }} className="data_column">
                                                                                                                {bathroom?.sub_feature?.sub_feature_name ?? "---"}
                                                                                                            </td>

                                                                                                            <td style={{ width: "12%" }} className="data_column">
                                                                                                                {bathroom?.name ?? "---"}
                                                                                                            </td>

                                                                                                            <td style={{ width: "12%" }} className="data_column">
                                                                                                                {bathroom?.brand?.brand_name ?? "---"}
                                                                                                            </td>

                                                                                                            <td style={{ width: "12%" }} className="data_column">
                                                                                                                {bathroom?.color?.color_name ?? "---"}
                                                                                                            </td>

                                                                                                            <td style={{ width: "12%" }} className="data_column text-center">
                                                                                                                {bathroom?.size ?? "---"}
                                                                                                            </td>

                                                                                                            <td style={{ width: "15%" }} className="data_column">
                                                                                                                {bathroom?.link ? (
                                                                                                                    <Link to={bathroom?.link} target="_blank">
                                                                                                                        {bathroom?.link ?? "---"}
                                                                                                                    </Link>
                                                                                                                ) : (
                                                                                                                    <span>---</span>
                                                                                                                )}
                                                                                                            </td>

                                                                                                            <td style={{ width: "15%" }}>
                                                                                                                {tableFilesBody(bathroom?.file_list, 'Bathroom', false)}
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    </React.Fragment>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        ) : (
                                                                                            <tbody>
                                                                                                <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                    <td className="text-center text-muted">
                                                                                                        No {(bathroomFeature?.feature_name)?.toLowerCase()} bathroom details found.
                                                                                                    </td>
                                                                                                </tr>
                                                                                            </tbody>
                                                                                        )}
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        </React.Fragment>
                                                                    ))
                                                                )}
                                                            </React.Fragment>
                                                        ))
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Kitchen */}
                                {fsReportData && fsReportData?.data?.["Kitchen"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Kitchen"] = el)}>
                                        {reportContentHeader('Kitchen')}

                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Kitchen") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {((fsKitchenData && fsKitchenData?.length > 0) || (fsKitchenApplianceData && fsKitchenApplianceData?.length > 0)) && (
                                                        <React.Fragment>
                                                            {fsKitchenData?.map((kitchenData) => (
                                                                <React.Fragment key={`kitchenFeature_${kitchenData?.id}`}>
                                                                    <div className="fs_report_label">
                                                                        <h6>{kitchenData?.location_name}</h6>
                                                                    </div>

                                                                    {kitchenData?.data_kitchen && kitchenData?.data_kitchen?.length > 0 ? (
                                                                        (() => {
                                                                            const typeData = kitchenData?.data_kitchen?.filter(data => data?.category === 'Type');
                                                                            const baseUnitsData = kitchenData?.data_kitchen?.filter(data => data?.category === 'Base Units');
                                                                            const wallUnitsData = kitchenData?.data_kitchen?.filter(data => data?.category === 'Wall Units');
                                                                            const worktopData = kitchenData?.data_kitchen?.filter(data => data?.category === 'Worktop');
                                                                            const doorData = kitchenData?.data_kitchen?.filter(data => data?.category === 'Door');
                                                                            const handleData = kitchenData?.data_kitchen?.filter(data => data?.category === 'Handle');

                                                                            return (
                                                                                <React.Fragment>
                                                                                    {/* Type */}
                                                                                    <React.Fragment>
                                                                                        <div className="fs_report_sub_label">
                                                                                            <h6>
                                                                                                <i className="bi bi-arrow-return-right me-2"></i>
                                                                                                Type
                                                                                            </h6>
                                                                                        </div>

                                                                                        <div className="fs_report_table_area">
                                                                                            <div className="table-responsive">
                                                                                                <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                                    {typeData && typeData?.length > 0 ? (
                                                                                                        <tbody>
                                                                                                            {typeData?.map((kitchenType) => (
                                                                                                                <React.Fragment key={`kitchenType_${kitchenType?.id}`}>
                                                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                        <td style={{ width: "10%" }}>
                                                                                                                            {tableImageBody(kitchenType?.image_path || null)}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {kitchenType?.brand?.brand_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {kitchenType?.collection?.kitchen_collection_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {kitchenType?.range?.range_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {kitchenType?.color?.color_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {kitchenType?.kitchen_style?.style_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {kitchenType?.finish?.finish_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenType?.link ? (
                                                                                                                                <Link to={kitchenType?.link} target="_blank">
                                                                                                                                    {kitchenType?.link ?? "---"}
                                                                                                                                </Link>
                                                                                                                            ) : (
                                                                                                                                <span>---</span>
                                                                                                                            )}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }}>
                                                                                                                            {tableFilesBody(kitchenType?.file_list, 'Kitchen', false)}
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                </React.Fragment>
                                                                                                            ))}
                                                                                                        </tbody>
                                                                                                    ) : (
                                                                                                        <tbody>
                                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                <td className="text-center text-muted">
                                                                                                                    No kitchen type details found.
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        </tbody>
                                                                                                    )}
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>
                                                                                    </React.Fragment>

                                                                                    {/* Base units */}
                                                                                    <React.Fragment>
                                                                                        <div className="fs_report_sub_label">
                                                                                            <h6>
                                                                                                <i className="bi bi-arrow-return-right me-2"></i>
                                                                                                Base units
                                                                                            </h6>
                                                                                        </div>

                                                                                        <div className="fs_report_table_area">
                                                                                            <div className="table-responsive">
                                                                                                <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                                    {baseUnitsData && baseUnitsData?.length > 0 ? (
                                                                                                        <tbody>
                                                                                                            {baseUnitsData?.map((baseUnit) => (
                                                                                                                <React.Fragment key={`baseUnit_${baseUnit?.id}`}>
                                                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                        <td style={{ width: "10%" }}>
                                                                                                                            {tableImageBody(baseUnit?.image_path || null)}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {baseUnit?.brand?.brand_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {baseUnit?.collection?.kitchen_collection_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {baseUnit?.range?.range_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {baseUnit?.color?.color_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {baseUnit?.kitchen_style?.style_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {baseUnit?.finish?.finish_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {baseUnit?.link ? (
                                                                                                                                <Link to={baseUnit?.link} target="_blank">
                                                                                                                                    {baseUnit?.link ?? "---"}
                                                                                                                                </Link>
                                                                                                                            ) : (
                                                                                                                                <span>---</span>
                                                                                                                            )}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }}>
                                                                                                                            {tableFilesBody(baseUnit?.file_list, 'Kitchen', false)}
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                </React.Fragment>
                                                                                                            ))}
                                                                                                        </tbody>
                                                                                                    ) : (
                                                                                                        <tbody>
                                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                <td className="text-center text-muted">
                                                                                                                    No kitchen base units details found.
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        </tbody>
                                                                                                    )}
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>
                                                                                    </React.Fragment>

                                                                                    {/* Wall units */}
                                                                                    <React.Fragment>
                                                                                        <div className="fs_report_sub_label">
                                                                                            <h6>
                                                                                                <i className="bi bi-arrow-return-right me-2"></i>
                                                                                                Wall units
                                                                                            </h6>
                                                                                        </div>

                                                                                        <div className="fs_report_table_area">
                                                                                            <div className="table-responsive">
                                                                                                <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                                    {wallUnitsData && wallUnitsData?.length > 0 ? (
                                                                                                        <tbody>
                                                                                                            {wallUnitsData?.map((wallUnit) => (
                                                                                                                <React.Fragment key={`wallUnit_${wallUnit?.id}`}>
                                                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                        <td style={{ width: "10%" }}>
                                                                                                                            {tableImageBody(wallUnit?.image_path || null)}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {wallUnit?.brand?.brand_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {wallUnit?.collection?.kitchen_collection_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {wallUnit?.range?.range_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {wallUnit?.color?.color_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {wallUnit?.kitchen_style?.style_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "10%" }} className="data_column">
                                                                                                                            {wallUnit?.finish?.finish_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {wallUnit?.link ? (
                                                                                                                                <Link to={wallUnit?.link} target="_blank">
                                                                                                                                    {wallUnit?.link ?? "---"}
                                                                                                                                </Link>
                                                                                                                            ) : (
                                                                                                                                <span>---</span>
                                                                                                                            )}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }}>
                                                                                                                            {tableFilesBody(wallUnit?.file_list, 'Kitchen', false)}
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                </React.Fragment>
                                                                                                            ))}
                                                                                                        </tbody>
                                                                                                    ) : (
                                                                                                        <tbody>
                                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                <td className="text-center text-muted">
                                                                                                                    No kitchen wall units details found.
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        </tbody>
                                                                                                    )}
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>
                                                                                    </React.Fragment>

                                                                                    {/* Worktop */}
                                                                                    <React.Fragment>
                                                                                        <div className="fs_report_sub_label">
                                                                                            <h6>
                                                                                                <i className="bi bi-arrow-return-right me-2"></i>
                                                                                                Worktop
                                                                                            </h6>
                                                                                        </div>

                                                                                        <div className="fs_report_table_area">
                                                                                            <div className="table-responsive">
                                                                                                <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                                    {worktopData && worktopData?.length > 0 ? (
                                                                                                        <tbody>
                                                                                                            {worktopData?.map((kitchenWorktop) => (
                                                                                                                <React.Fragment key={`kitchenWorktop_${kitchenWorktop?.id}`}>
                                                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                        <td style={{ width: "10%" }}>
                                                                                                                            {tableImageBody(kitchenWorktop?.image_path || null)}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenWorktop?.brand?.brand_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenWorktop?.name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenWorktop?.type?.type_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            <small>
                                                                                                                                {kitchenWorktop.size_depth ? `Depth: ${String(kitchenWorktop.size_depth).padStart(2, '0')}` : '---'} <br />
                                                                                                                                {kitchenWorktop.size_thickness ? `Thickness: ${String(kitchenWorktop.size_thickness).padStart(2, '0')}` : '---'}
                                                                                                                            </small>
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenWorktop?.link ? (
                                                                                                                                <Link to={kitchenWorktop?.link} target="_blank">
                                                                                                                                    {kitchenWorktop?.link ?? "---"}
                                                                                                                                </Link>
                                                                                                                            ) : (
                                                                                                                                <span>---</span>
                                                                                                                            )}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }}>
                                                                                                                            {tableFilesBody(kitchenWorktop?.file_list, 'Kitchen', false)}
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                </React.Fragment>
                                                                                                            ))}
                                                                                                        </tbody>
                                                                                                    ) : (
                                                                                                        <tbody>
                                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                <td className="text-center text-muted">
                                                                                                                    No kitchen worktop details found.
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        </tbody>
                                                                                                    )}
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>
                                                                                    </React.Fragment>


                                                                                    {/* Door */}
                                                                                    <React.Fragment>
                                                                                        <div className="fs_report_sub_label">
                                                                                            <h6>
                                                                                                <i className="bi bi-arrow-return-right me-2"></i>
                                                                                                Door
                                                                                            </h6>
                                                                                        </div>

                                                                                        <div className="fs_report_table_area">
                                                                                            <div className="table-responsive">
                                                                                                <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                                    {doorData && doorData?.length > 0 ? (
                                                                                                        <tbody>
                                                                                                            {doorData?.map((kitchenDoor) => (
                                                                                                                <React.Fragment key={`kitchenDoor_${kitchenDoor?.id}`}>
                                                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                        <td style={{ width: "10%" }}>
                                                                                                                            {tableImageBody(kitchenDoor?.image_path || null)}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenDoor?.brand?.brand_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenDoor?.name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenDoor?.kitchen_style?.style_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenDoor?.color?.color_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenDoor?.link ? (
                                                                                                                                <Link to={kitchenDoor?.link} target="_blank">
                                                                                                                                    {kitchenDoor?.link ?? "---"}
                                                                                                                                </Link>
                                                                                                                            ) : (
                                                                                                                                <span>---</span>
                                                                                                                            )}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }}>
                                                                                                                            {tableFilesBody(kitchenDoor?.file_list, 'Kitchen', false)}
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                </React.Fragment>
                                                                                                            ))}
                                                                                                        </tbody>
                                                                                                    ) : (
                                                                                                        <tbody>
                                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                <td className="text-center text-muted">
                                                                                                                    No kitchen door details found.
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        </tbody>
                                                                                                    )}
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>
                                                                                    </React.Fragment>


                                                                                    {/* Handle */}
                                                                                    <React.Fragment>
                                                                                        <div className="fs_report_sub_label">
                                                                                            <h6>
                                                                                                <i className="bi bi-arrow-return-right me-2"></i>
                                                                                                Handle
                                                                                            </h6>
                                                                                        </div>

                                                                                        <div className="fs_report_table_area">
                                                                                            <div className="table-responsive">
                                                                                                <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                                    {handleData && handleData?.length > 0 ? (
                                                                                                        <tbody>
                                                                                                            {handleData?.map((kitchenHandle) => (
                                                                                                                <React.Fragment key={`kitchenHandle_${kitchenHandle?.id}`}>
                                                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                        <td style={{ width: "10%" }}>
                                                                                                                            {tableImageBody(kitchenHandle?.image_path || null)}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column">
                                                                                                                            {kitchenHandle?.brand?.brand_name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "20%" }} className="data_column">
                                                                                                                            {kitchenHandle?.name ?? "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "15%" }} className="data_column text-center">
                                                                                                                            {kitchenHandle?.number ? String(kitchenHandle?.number).padStart(2, '0') : "---"}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "20%" }} className="data_column">
                                                                                                                            {kitchenHandle?.link ? (
                                                                                                                                <Link to={kitchenHandle?.link} target="_blank">
                                                                                                                                    {kitchenHandle?.link ?? "---"}
                                                                                                                                </Link>
                                                                                                                            ) : (
                                                                                                                                <span>---</span>
                                                                                                                            )}
                                                                                                                        </td>

                                                                                                                        <td style={{ width: "20%" }}>
                                                                                                                            {tableFilesBody(kitchenHandle?.file_list, 'Kitchen', false)}
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                </React.Fragment>
                                                                                                            ))}
                                                                                                        </tbody>
                                                                                                    ) : (
                                                                                                        <tbody>
                                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                                <td className="text-center text-muted">
                                                                                                                    No kitchen handle details found.
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        </tbody>
                                                                                                    )}
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>
                                                                                    </React.Fragment>
                                                                                </React.Fragment>
                                                                            )
                                                                        })()
                                                                    ) : (
                                                                        <div className="fs_report_table_area">
                                                                            <div className="table-responsive">
                                                                                <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                    <tbody>
                                                                                        <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                            <td className="text-center text-muted">
                                                                                                No kitchen details found.
                                                                                            </td>
                                                                                        </tr>
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}

                                                            {fsKitchenApplianceData?.map((kitchenApplianceData) => (
                                                                <React.Fragment key={`kitchenFeature_${kitchenApplianceData?.id}`}>
                                                                    <div className="fs_report_sub_label">
                                                                        <h6>
                                                                            <i className="bi bi-arrow-return-right me-2"></i>
                                                                            {kitchenApplianceData?.location_name}
                                                                        </h6>
                                                                    </div>

                                                                    <div className="fs_report_table_area">
                                                                        <div className="table-responsive">
                                                                            <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                                {kitchenApplianceData?.data_kitchen && kitchenApplianceData?.data_kitchen?.length > 0 ? (
                                                                                    <tbody>
                                                                                        {kitchenApplianceData?.data_kitchen?.map((kitchenAppliance) => (
                                                                                            <React.Fragment key={`kitchenAppliance_${kitchenAppliance?.id}`}>
                                                                                                <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                    <td style={{ width: "10%" }}>
                                                                                                        {tableImageBody(kitchenAppliance?.image_path || null)}
                                                                                                    </td>

                                                                                                    <td style={{ width: "15%" }} className="data_column">
                                                                                                        {kitchenAppliance?.appliance?.appliance_name ?? "---"}
                                                                                                    </td>

                                                                                                    <td style={{ width: "15%" }} className="data_column">
                                                                                                        {kitchenAppliance?.brand?.brand_name ?? "---"}
                                                                                                    </td>

                                                                                                    <td style={{ width: "15%" }} className="data_column">
                                                                                                        {kitchenAppliance?.name ?? "---"}
                                                                                                    </td>

                                                                                                    <td style={{ width: "15%" }} className="data_column">
                                                                                                        {kitchenAppliance?.color?.color_name ?? "---"}
                                                                                                    </td>

                                                                                                    <td style={{ width: "15%" }} className="data_column">
                                                                                                        {kitchenAppliance?.link ? (
                                                                                                            <Link to={kitchenAppliance?.link} target="_blank">
                                                                                                                {kitchenAppliance?.link ?? "---"}
                                                                                                            </Link>
                                                                                                        ) : (
                                                                                                            <span>---</span>
                                                                                                        )}
                                                                                                    </td>

                                                                                                    <td style={{ width: "15%" }}>
                                                                                                        {tableFilesBody(kitchenAppliance?.file_list, 'Kitchen', false)}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            </React.Fragment>
                                                                                        ))}
                                                                                    </tbody>
                                                                                ) : (
                                                                                    <tbody>
                                                                                        <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                            <td className="text-center text-muted">
                                                                                                No kitchen {(kitchenApplianceData?.location_name)?.toLowerCase()} details found.
                                                                                            </td>
                                                                                        </tr>
                                                                                    </tbody>
                                                                                )}
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </React.Fragment>
                                                            ))}
                                                        </React.Fragment>
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Tiles */}
                                {fsReportData && fsReportData?.data?.["Tiles"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Tiles"] = el)}>
                                        {reportContentHeader('Tiles')}


                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Tiles") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {fsTileData && fsTileData?.length > 0 && (
                                                        fsTileData?.map((tileData) => (
                                                            <React.Fragment key={`tileFeature_${tileData?.id}`}>
                                                                <div className="fs_report_label">
                                                                    <h6>{tileData?.location_name}</h6>
                                                                </div>

                                                                <div className="fs_report_table_area">
                                                                    <div className="table-responsive">
                                                                        <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                            {tileData?.data_tile && tileData?.data_tile?.length > 0 ? (
                                                                                <tbody>
                                                                                    {tileData?.data_tile?.map((tile) => (
                                                                                        <React.Fragment key={`tile_${tile?.id}`}>
                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                <td style={{ width: "10%" }}>
                                                                                                    {tableImageBody(tile?.image_path || null)}
                                                                                                </td>

                                                                                                <td style={{ width: "12%" }} className="data_column">
                                                                                                    {tile?.shop?.shop_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "12%" }} className="data_column">
                                                                                                    {tile?.name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "10%" }} className="data_column">
                                                                                                    {tile?.type?.type_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "8%" }} className="data_column text-center">
                                                                                                    {tile?.size ? String(tile?.size).padStart(2, '0') : "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "10%" }} className="data_column">
                                                                                                    {tile?.color?.color_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "8%" }} className="data_column text-center">
                                                                                                    {tile?.quantity ? String(tile?.quantity).padStart(2, '0') : "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {tile?.link ? (
                                                                                                        <Link to={tile?.link} target="_blank">
                                                                                                            {tile?.link ?? "---"}
                                                                                                        </Link>
                                                                                                    ) : (
                                                                                                        <span>---</span>
                                                                                                    )}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }}>
                                                                                                    {tableFilesBody(tile?.file_list, 'Tiles', false)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        </React.Fragment>
                                                                                    ))}
                                                                                </tbody>
                                                                            ) : (
                                                                                <tbody>
                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                        <td className="text-center text-muted">
                                                                                            No {(tileData?.location_name)?.toLowerCase()} tiles details found.
                                                                                        </td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            )}
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        ))
                                                    )}

                                                    {tileAccessoryData && (
                                                        accessoryDataBody(tileAccessoryData?.data_accessory, 'Tiles')
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Floor Finishing */}
                                {fsReportData && fsReportData?.data?.["Floor Finishing"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Floor Finishing"] = el)}>
                                        {reportContentHeader('Floor Finishing')}

                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Floor Finishing") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    <div className="fs_report_label">
                                                        <h6>Floor Finishing</h6>
                                                    </div>

                                                    <div className="fs_report_table_area">
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                {fsFloorFinishingData && fsFloorFinishingData?.length > 0 ? (
                                                                    <tbody>
                                                                        {fsFloorFinishingData?.map((floorFinishing) => (
                                                                            <React.Fragment key={`floorFinishing_${floorFinishing?.id}`}>
                                                                                <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                    <td style={{ width: "10%" }}>
                                                                                        {tableImageBody(floorFinishing?.image_path || null)}
                                                                                    </td>

                                                                                    <td style={{ width: "12%" }} className="data_column">
                                                                                        {floorFinishing?.location?.location_name ?? "---"}
                                                                                    </td>

                                                                                    <td style={{ width: "12%" }} className="data_column">
                                                                                        {floorFinishing?.brand?.brand_name ?? "---"}
                                                                                    </td>

                                                                                    <td style={{ width: "12%" }} className="data_column">
                                                                                        {floorFinishing?.name ?? "---"}
                                                                                    </td>

                                                                                    <td style={{ width: "12%" }} className="data_column">
                                                                                        {floorFinishing?.type?.type_name ?? "---"}
                                                                                    </td>

                                                                                    <td style={{ width: "12%" }} className="data_column note_column">
                                                                                        {floorFinishing?.note ?? "---"}
                                                                                    </td>

                                                                                    <td style={{ width: "15%" }} className="data_column">
                                                                                        {floorFinishing?.link ? (
                                                                                            <Link to={floorFinishing?.link} target="_blank">
                                                                                                {floorFinishing?.link ?? "---"}
                                                                                            </Link>
                                                                                        ) : (
                                                                                            <span>---</span>
                                                                                        )}
                                                                                    </td>

                                                                                    <td style={{ width: "15%" }}>
                                                                                        {tableFilesBody(floorFinishing?.file_list, 'Floor Finishing', false)}
                                                                                    </td>
                                                                                </tr>
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </tbody>
                                                                ) : (
                                                                    <tbody>
                                                                        <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                            <td className="text-center text-muted">
                                                                                No floor finishing details found.
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                )}
                                                            </table>
                                                        </div>
                                                    </div>

                                                    {hasFloorFinishingAccessoryOption && (
                                                        accessoryDataBody(floorFinishingAccessoryData, 'Floor Finishing')
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Other */}
                                {fsReportData && fsReportData?.data?.["Others"] && (
                                    <div className="fs_report_content" ref={(el) => (sectionRefs.current["Others"] = el)}>
                                        {reportContentHeader('Others')}

                                        <AnimatePresence initial={false}>
                                            {expandedSections.includes("Others") && (
                                                <motion.div
                                                    className="fs_report_body"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    {fsOtherData && fsOtherData?.length > 0 && (
                                                        fsOtherData?.map((otherData) => (
                                                            <React.Fragment key={`otherFeature_${otherData?.id}`}>
                                                                <div className="fs_report_label">
                                                                    <h6>{otherData?.location_name}</h6>
                                                                </div>

                                                                <div className="fs_report_table_area">
                                                                    <div className="table-responsive">
                                                                        <table className="table table-bordered sv_report_table pl_table mb-0">
                                                                            {otherData?.data_other && otherData?.data_other?.length > 0 ? (
                                                                                <tbody>
                                                                                    {otherData?.data_other?.map((other) => (
                                                                                        <React.Fragment key={`other_${other?.id}`}>
                                                                                            <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                                <td style={{ width: "10%" }}>
                                                                                                    {tableImageBody(other?.image_path || null)}
                                                                                                </td>

                                                                                                <td style={{ width: "20%" }} className="data_column">
                                                                                                    {other?.flooring_screed ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "20%" }} className="data_column">
                                                                                                    {other?.brand?.brand_name ?? "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column text-center">
                                                                                                    {other?.number ? String(other?.number).padStart(2, '0') : "---"}
                                                                                                </td>

                                                                                                <td style={{ width: "15%" }} className="data_column">
                                                                                                    {other?.link ? (
                                                                                                        <Link to={other?.link} target="_blank">
                                                                                                            {other?.link ?? "---"}
                                                                                                        </Link>
                                                                                                    ) : (
                                                                                                        <span>---</span>
                                                                                                    )}
                                                                                                </td>

                                                                                                <td style={{ width: "20%" }}>
                                                                                                    {tableFilesBody(other?.file_list, 'Others', false)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        </React.Fragment>
                                                                                    ))}
                                                                                </tbody>
                                                                            ) : (
                                                                                <tbody>
                                                                                    <tr className="sv_report_table_data fs_data_row quotation_table_data_row_sub">
                                                                                        <td className="text-center text-muted">
                                                                                            No {(otherData?.location_name)?.toLowerCase()} other details found.
                                                                                        </td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            )}
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        ))
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                            </article>
                        ) : (
                            <div className="page_main_content_section no_header">
                                <NoDataCreated
                                    message={`No data available!`}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Report config modal */}
            <FSReportConfigModal
                isOpen={showReportConfigModal}
                onClose={handleCloseReportConfigModal}
                fsData={fsData}
                defaultSelectedOptions={selectedFeatureOptions}
                onPreview={handlePreviewUpdate}
            />
            {/*  */}


            {/* Mail modal */}
            <Dialog
                header={mailModalHeader}
                footer={mailModalFooter}
                headerClassName="custom_modal_header"
                visible={showMailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseMailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                {emailAddresses.map((email, index) => (
                                    <div className="mail_input_area d-flex align-items-center mb-2" key={index}>
                                        <TextInput
                                            id={`emailAddress-${index}`}
                                            label={`E-mail address ${index + 1}`}
                                            labelHtmlFor={`emailAddress-${index}`}
                                            required={true}
                                            value={email.value}
                                            placeholder="abcd@gmail.com"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleEmailChange(index, e.target.value)
                                            }
                                            error={email.error}
                                            keyFilter={'email'}
                                            formGroupClassName="mb-1"
                                        />
                                        {emailAddresses.length > 1 && (
                                            <i
                                                className="bi bi-dash-circle mail_remove_btn"
                                                title="Remove"
                                                onClick={() => handleRemoveMail(index)}
                                            ></i>
                                        )}
                                    </div>
                                ))}

                                <button className={`add_data_btn p-ripple`} onClick={handleAddMail}>
                                    <i className={`bi bi-plus-circle`}></i>
                                    Add mail
                                    < Ripple />
                                </button>
                            </div>

                            <div className="col-12 mt-3">
                                {fsReportData?.fs_report_download_path !== null && (
                                    <div
                                        className="file_attached_area"
                                        onClick={handleViewReportDocument}
                                    >
                                        <i className="bi bi-paperclip"></i>
                                        <h6>Finishing schedule report.pdf</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={fileUrl}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default FinishingScheduleReport;